import { supabase } from '../lib/supabase'
import { fetchAllByRange, SUPABASE_IN_CHUNK_SIZE } from '../lib/supabaseFetchAll'
import type { CctBuscaAditivo, CctBuscaLista, CctUltimaBuscaAditivo } from '../types/cctBuscas'

export type CctBuscasPeriodoParams = {
  dataInicio: string
  dataFim: string
}

type CctConvencaoCnpj = {
  id: number
  cnpj_entidade_laboral: string | null
  cnpj_entidade_patronal: string | null
}

const toCreatedAtRange = (dataInicio: string, dataFim: string) => ({
  inicio: new Date(`${dataInicio}T00:00:00`).toISOString(),
  fim: new Date(`${dataFim}T23:59:59`).toISOString(),
})

async function fetchConvencoesByIds(ids: number[]): Promise<Map<number, CctConvencaoCnpj>> {
  const map = new Map<number, CctConvencaoCnpj>()
  const uniqueIds = Array.from(new Set(ids.filter((id) => id != null)))

  for (let i = 0; i < uniqueIds.length; i += SUPABASE_IN_CHUNK_SIZE) {
    const chunk = uniqueIds.slice(i, i + SUPABASE_IN_CHUNK_SIZE)
    const { data, error } = await supabase
      .from('cct_convencoes')
      .select('id, cnpj_entidade_laboral, cnpj_entidade_patronal')
      .in('id', chunk)

    if (error) throw error

    for (const row of (data ?? []) as CctConvencaoCnpj[]) {
      map.set(row.id, row)
    }
  }

  return map
}

export const cctBuscasService = {
  async getBuscasCcts(params: CctBuscasPeriodoParams): Promise<CctBuscaLista[]> {
    const { inicio, fim } = toCreatedAtRange(params.dataInicio, params.dataFim)

    const res = await fetchAllByRange<Record<string, unknown>>((from, to) =>
      supabase
        .from('cct_buscas')
        .select('*')
        .gte('created_at', inicio)
        .lte('created_at', fim)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to),
    )

    if (res.error) throw new Error(res.error)

    const cctBuscas = res.data
    const convencaoIds = cctBuscas
      .map((busca) => busca.cct_convencao_id as number | null)
      .filter((id): id is number => id != null)
    const convencoesById = await fetchConvencoesByIds(convencaoIds)

    return cctBuscas.map((busca) => {
      const convencaoId = (busca.cct_convencao_id as number | null) ?? null
      const convencao = convencaoId != null ? convencoesById.get(convencaoId) : undefined
      return {
        id: busca.id as number,
        cct_convencao_id: convencaoId,
        resposta: busca.resposta,
        created_at: busca.created_at as string,
        cnpj_entidade_laboral: convencao?.cnpj_entidade_laboral ?? '',
        cnpj_entidade_patronal: convencao?.cnpj_entidade_patronal ?? '',
        status: (busca.status as string | null) ?? null,
        link_documento: (busca.link_documento as string | null) ?? null,
        link_download: (busca.link_download as string | null) ?? null,
        vigencia_inicio: (busca.vigencia_inicio as string | null) ?? null,
        vigencia_fim: (busca.vigencia_fim as string | null) ?? null,
        partes: (busca.partes as string | null) ?? null,
        codigo: (busca.codigo as string | null) ?? null,
        data_importacao: (busca.data_importacao as string | null) ?? null,
        id_scgp: (busca.id_scgp as number | string | null) ?? null,
        clausulas_especificas: (busca.clausulas_especificas as string | null) ?? null,
      }
    })
  },

  async getBuscasAditivos(params: CctBuscasPeriodoParams): Promise<CctBuscaAditivo[]> {
    const { inicio, fim } = toCreatedAtRange(params.dataInicio, params.dataFim)

    const res = await fetchAllByRange<CctBuscaAditivo>((from, to) =>
      supabase
        .from('cct_buscas_aditivos')
        .select('*')
        .gte('created_at', inicio)
        .lte('created_at', fim)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to),
    )

    if (res.error) throw new Error(res.error)
    return res.data
  },

  async getBuscasAditivosByConvencaoIds(convencaoIds: number[]): Promise<CctBuscaAditivo[]> {
    if (convencaoIds.length === 0) return []
    const { data, error } = await supabase
      .from('cct_buscas_aditivos')
      .select('*')
      .in('cct_convencao_id', convencaoIds)
      .order('created_at', { ascending: false })

    if (error) throw error
    return (data ?? []) as CctBuscaAditivo[]
  },

  async getUltimaBuscaAditivoPorConvencaoIds(convencaoIds: number[]): Promise<CctUltimaBuscaAditivo[]> {
    if (convencaoIds.length === 0) return []
    const { data, error } = await supabase.rpc('rpc_cct_ultima_busca_aditivo_por_convencao', {
      p_convencao_ids: convencaoIds
    })
    if (error) throw error
    return ((data as { cct_convencao_id?: number; status?: string | null; created_at?: string }[] | null) ?? [])
      .filter((row): row is { cct_convencao_id: number; status: string | null; created_at: string } => row?.cct_convencao_id != null)
      .map((row) => ({
        cct_convencao_id: Number(row.cct_convencao_id),
        status: row.status ?? null,
        created_at: row.created_at ?? ''
      }))
  },

  async atualizarStatusAditivoLocalizadaSemAlteracao(
    id: string,
    novoStatus: 'DISPONÍVEL PARA IMPORTAR' | 'SEM ALTERAÇÃO',
  ): Promise<number> {
    const { data, error } = await supabase.rpc('rpc_cct_atualizar_status_aditivo_localizada_sem_alteracao', {
      p_busca_id: id,
      p_novo_status: novoStatus,
    })
    if (error) throw error
    return Number(data ?? 0)
  },
}
